<template>
  <Highcharts
    ref="chart"
    :options="chartOptions"
  />
</template>

<script>
import { parseISO, getTime } from 'date-fns';
import { Chart } from 'highcharts-vue';

import { formatterDateLabel, formatterTooltip } from './chart-helper.js';

export default {
  name: 'AreaSpline',
  components: {
    Highcharts: Chart,
  },
  props: {
    series: {
      type: Array,
      required: true,
    },
    resize: {
      type: Boolean,
      default: false,
    },
    tooltipConfig: {
      type: Object,
      default() {
        return {
          addHeader: true,
          addFooter: true,
          addPercentVariation: true,
        };
      },
    },
    overrideChartOptions: {
      type: Object,
      default: () => ({}),
    },
    periodLabel: {
      type: String,
      required: true,
    },
  },
  computed: {
    chartOptions() {
      const periodLabel = this.periodLabel;

      const tooltipConfig = {
        ...this.tooltipConfig,
        period: periodLabel,
      };

      const getMiliSecondsTimeStamp = (date) => getTime(parseISO(date));

      const series = this.series.map(serie => {
        return {
          ...serie,
          data: serie.data.map(dataItem => ({
            ...dataItem,
            x: getMiliSecondsTimeStamp(dataItem.date),
            y: dataItem.y,
          })),
        };
      });

      return {
        credits: {
          enabled: false,
        },
        yAxis: {
          title: {
            enabled: false,
          },
          labels: {
            x: -16,
            style: {
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 16,
              color: 'var(--ds-color-text-2)',
            },
          },
        },
        legend: {
          symbolHeight: 10,
          symbolWidth: 10,
          symbolRadius: 10,
          squareSymbol: false,
          align: 'left',
          margin: 16,
          padding: 0,
        },
        lang: {
          decimalPoint: ',',
          thousandsSep: '.',
        },
        tooltip: {
          shared: true,
          useHTML: true,
          shadow: false,
          borderRadius: 4,
          formatter() {
            return formatterTooltip(this, tooltipConfig);
          },
        },
        chart: {
          type: 'areaspline',
          styleMode: true,
          borderRadius: 6,
          spacingBottom: 3,
          spacingLeft: 0,
          spacingRight: 0,
        },
        xAxis: {
          type: 'datetime',
          startOfWeek: 0,
          tickWidth: 0,
          crosshair: true,
          labels: {
            formatter() {
              return formatterDateLabel(this.value);
            },
            y: 38,
            style: {
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: 16,
              color: 'var(--ds-color-text-2)',
            },
          },
        },
        series,
        plotOptions: {
          series: {
            fillOpacity: 0,
            cursor: 'pointer',
            marker: {
              enabled: false,
              symbol: 'circle',
            },
            events: {
              legendItemClick: (e) => {
                this.$emit('legend-click', { legend: e.target.name });
              },
            },
          },
        },
        ...this.overrideChartOptions,
      };
    },
  },
  watch: {
    resize() {
      this.$refs.chart.chart.reflow();
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  .highcharts {
    &-tooltip {
      border-radius: var(--ds-border-radius-sm);
    }

    &-tooltip-box {
      stroke-width: 0;
      fill: var(--ds-color-neutral);
      stroke: var(--ds-color-neutral);
      fill-opacity: 1;
    }

    &-title {
      display: none;
    }

    &-subtitle {
      display: none;
    }

    &-axis-line {
      margin-bottom: 16px;
    }
  }

  .highcharts-custom-tooltip {
    box-shadow: var(--ds-light-dropshadow-1);
    margin: 0;
    padding: var(--ds-spacing-stack-xs);
    border-radius: var(--ds-border-radius-sm);

    &__header {
      color: var(--ds-color-text-2);
      font-size: var(--ds-font-size-xs);
      margin-bottom: var(--ds-spacing-stack-nano);
    }

    &__ds-icon {
      background: var(--ds-color-icon-3);
      mask-position: center center;
      mask-repeat: no-repeat;
      mask-size: cover;
      vertical-align: middle;
      width: var(--ds-font-size-sm);
      height: var(--ds-font-size-sm);
      margin: 0 2px;
    }

    &__content {
      line-height: 25px;
    }

    &__divider {
      margin-top: var(--ds-spacing-stack-nano);
      height: 1px;
      border-width: 0;
      background-color: var(--ds-color-overlay);
    }

    &__footer {
      display: grid;
      grid-template-columns: 25px 1fr;
      font-size: var(--ds-font-size-xs);
      margin-top: var(--ds-spacing-stack-xs);
      white-space: normal;
      color: var(--ds-color-text-2);

      & p {
        margin: 0;
      }
    }

    &__ds-icon-info {
      background: var(--ds-color-icon-3);
      mask-position: center center;
      mask-repeat: no-repeat;
      mask-size: cover;
      width: var(--ds-font-size-baseline);
      height: var(--ds-font-size-baseline);
    }

    &__item-wrapper {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: var(--ds-spacing-stack-md);
      justify-content: space-between;
    }

    &__item {
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }

    &__legend {
      span {
        color: var(--ds-color-text-1);
        font-size: var(--ds-font-size-xs);
      }

      &::before {
        content: '';
        display: inline-block;
        width: var(--ds-spacing-stack-nano);
        height: var(--ds-spacing-stack-nano);
        margin-right: var(--ds-spacing-stack-nano);
        border-radius: 50%;
        background-color: currentColor;
      }
    }

    &__variation-grid {
      display: grid;
      grid-template-columns: 2fr var(--ds-spacing-stack-xs) minmax(42px, auto);
      align-items: center;
      gap: 4px;
    }

    &__variation {
      color: var(--ds-color-text-1);
      font-weight: var(--ds-font-weight-semibold);
      font-size: var(--ds-font-size-xs);
      text-align: right;
    }

    &__variation-value {
      font-weight: var(--ds-font-weight-semibold);
      font-size: var(--ds-font-size-xs);
      text-align: left;
    }
  }
}
</style>
